import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { PageHeader } from "../../../components/ui";
import {
  ShiftTimingFields,
  WorkingHourThresholdFields,
  OvertimeRuleFields,
  AttendanceControlFields } from
"../components";
import { getInitialFormState } from "../constants/shiftFormState";
import { useShiftValidation } from "../hooks/useShiftValidation";
import { toHHMM } from "../utils/shiftTimeUtils";
import {
  fetchAllShifts,
  fetchShiftById,
  createShiftOnServer,
  updateShiftOnServer } from
"../../../services/shiftService";
import { TOAST_KEY } from "./ShiftsListPage";
import { AppLayout, PageBackLink } from "../../../components/layout";


const fieldLabelClass = "block text-xs font-semibold per-file-hint mb-1.5";
const inputClass =
"w-full h-10 px-3 rounded-xl border bg-white text-sm outline-hidden focus:ring-1 focus:ring-accent";

function YesNoToggle({ label, value, onChange }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className={fieldLabelClass}>{label}</span>
      <div className="inline-flex rounded-xl border border-(--color-text-border) p-0.5 w-fit">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`h-8 px-4 rounded-[10px] text-xs font-semibold transition ${
          value ? "bg-primary text-white" : "per-file-hint hover:text-foreground"}`
          }>
          <Trans i18nKey="common.yes" />
        
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`h-8 px-4 rounded-[10px] text-xs font-semibold transition ${
          !value ? "bg-primary text-white" : "per-file-hint hover:text-foreground"}`
          }>
          <Trans i18nKey="common.no" />
        
        </button>
      </div>
    </div>);

}

export function CreateEditShiftPage({ shiftId }) {
  const navigate = useNavigate();
  const isEditMode = Boolean(shiftId);

  const [formState, setFormState] = useState(getInitialFormState);
  const [existingShifts, setExistingShifts] = useState([]);
  const [loadingShift, setLoadingShift] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");

  const {
    fieldErrors,
    setFieldErrors,
    validateField,
    runGlobalValidation,
    isFormValid
  } = useShiftValidation(formState, existingShifts);

  useEffect(() => {
    fetchAllShifts()
    .then((data) => setExistingShifts(Array.isArray(data) ? data : data?.data ?? []))
    .catch(() => setExistingShifts([]));
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    fetchShiftById(shiftId)
    .then((shift) =>
    setFormState({
      ...getInitialFormState(),
      ...shift,
      id: shift?.id ?? shift?.shiftId ?? shiftId,
      shiftStartTime: toHHMM(shift?.shiftStartTime),
      shiftEndTime: toHHMM(shift?.shiftEndTime)
    })
    )
    .catch(() => setApiError("Failed to load shift."))
    .finally(() => setLoadingShift(false));
  }, [shiftId, isEditMode]);

  const pageTitle = isEditMode ? "Edit Shift" : "Create Shift";

  const handleSaveSubmit = async (e) => {
    e.preventDefault();

    const runtimeErrors = runGlobalValidation();
    if (Object.keys(runtimeErrors).length > 0) {
      return;
    }

    setSaving(true);
    setApiError("");

    // OT-hours values are ignored when Overtime Enabled = No.
    const payload = formState.overtimeEnabled ?
    formState :
    {
      ...formState,
      minimumOvertimeHours: "",
      halfDayOvertimeHours: "",
      fullDayOvertimeHours: ""
    };

    try {
      if (isEditMode) {
        await updateShiftOnServer(payload);
      } else {
        await createShiftOnServer(payload);
      }
      sessionStorage.setItem(
        TOAST_KEY,
        JSON.stringify({
          message: isEditMode ?
          `Shift "${formState.shiftName.trim()}" updated successfully.` :
          `Shift "${formState.shiftName.trim()}" created successfully.`,
          type: "success"
        })
      );
      navigate("/hrms-team/config/shifts");
    } catch (err) {
      const message = err?.message || "Failed to save shift. Please try again.";
      if (message.toLowerCase().includes("already exists") || message.toLowerCase().includes("already in use")) {
        setFieldErrors((prev) => ({ ...prev, name: message }));
      } else {
        setApiError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadingShift) {
    return (
      <AppLayout formLayout="standard">
        <div className="py-12 text-center text-sm per-file-hint">
          <Trans i18nKey="organizationShifts.loadingShift" />
        </div>
      </AppLayout>);

  }

  return (
    <AppLayout formLayout="standard">

      <PageHeader title={pageTitle} back={<PageBackLink to="/hrms-team/config/shifts" />} />

      <form onSubmit={handleSaveSubmit} className="w-full">
        {apiError &&
        <div className="flex items-center gap-2 rounded-xl border border-error/30 bg-error/5 px-3.5 py-2.5 text-[13px] text-error mb-4">
            {apiError}
          </div>
        }

        {/* 01 — Basic Details */}
        <div className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 overflow-hidden mb-5 form-surface form-section">
          <h3 className="text-lg font-medium text-foreground mb-4"><Trans i18nKey="organizationShifts.01BasicDetails" /></h3>
          <div className="grid grid-cols-1 gap-4 form-grid">
            <label className="flex flex-col gap-1.5 max-w-md">
              <span className={fieldLabelClass}>
                <Trans i18nKey="organizationShifts.shiftName" /> <span className="text-error">*</span>
              </span>
              <input
                type="text"
                className={`${inputClass} ${
                fieldErrors.shiftName ? "border-error" : "border-(--color-text-border)"}`
                }
                placeholder={i18n.t("examples.eGMorningProduction")}
                value={formState.shiftName}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormState((p) => ({ ...p, shiftName: value }));
                }}
                onBlur={(e) => validateField("shiftName", e.target.value, formState)} />
              
              {fieldErrors.shiftName &&
              <p className="text-[11px] text-error mt-1">{fieldErrors.shiftName}</p>
              }
            </label>

            <ShiftTimingFields
              formState={formState}
              setFormState={setFormState}
              fieldErrors={fieldErrors}
              validateField={validateField} />
            

            <YesNoToggle
              label="Is Active"
              value={formState.isActive}
              onChange={(v) => setFormState((p) => ({ ...p, isActive: v }))} />
            
          </div>
        </div>

        {/* 02 — Attendance Thresholds */}
        <div className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 overflow-hidden mb-5 form-surface form-section">
          <h3 className="text-lg font-medium text-foreground mb-4"><Trans i18nKey="organizationShifts.02AttendanceThresholds" /></h3>
          <WorkingHourThresholdFields
            formState={formState}
            setFormState={setFormState}
            fieldErrors={fieldErrors}
            validateField={validateField} />
          
        </div>

        {/* 03 — Overtime & Comp Off */}
        <div className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 overflow-hidden mb-5 form-surface form-section">
          <h3 className="text-lg font-medium text-foreground mb-4"><Trans i18nKey="dateTime.03OvertimeCompOff" /></h3>
          <OvertimeRuleFields
            formState={formState}
            setFormState={setFormState}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            validateField={validateField} />
          
        </div>

        {/* 04 — Attendance Controls */}
        <div className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 overflow-hidden mb-5 form-surface form-section">
          <h3 className="text-lg font-medium text-foreground mb-4"><Trans i18nKey="organizationShifts.04AttendanceControls" /></h3>
          <AttendanceControlFields formState={formState} setFormState={setFormState} />
        </div>

        <div className="flex justify-end gap-2.5 pt-1 form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/hrms-team/config/shifts")}
            disabled={saving}
            className="h-11 px-5 rounded-xl border-(--color-text-border) text-foreground hover:bg-workspace/40">
            <Trans i18nKey="formActions.cancel" />
          
          </Button>
          <Button
            type="submit"
            disabled={saving || !isFormValid}
            className="h-11 px-5 rounded-xl bg-primary text-white hover:bg-accent shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]">
            
            {saving ? "Saving..." : isEditMode ? "Save Changes" : "Create Shift"}
          </Button>
        </div>
      </form>
    </AppLayout>);

}

export function EditShiftRoutePage() {
  const { id } = useParams();
  return <CreateEditShiftPage shiftId={id} />;
}
